import { useEffect, useRef, useState } from 'react';

export interface OtpInputProps {
  /** Called once all six digits are present (after a short pause, like the design). */
  onComplete: (code: string) => void;
  onChange?: (code: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  label?: string;
}

const LENGTH = 6;

/**
 * Six single-digit boxes (`.otp`). Typing advances, Backspace on an empty box
 * goes back, and pasting or OS one-time-code autofill spreads the digits.
 * Remount (change `key`) to clear it.
 */
export function OtpInput({ onComplete, onChange, disabled, autoFocus, label = '인증 코드 6자리' }: OtpInputProps) {
  const [digits, setDigits] = useState<string[]>(() => Array.from({ length: LENGTH }, () => ''));
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const completed = useRef<string | null>(null);
  const handlers = useRef({ onComplete, onChange });
  useEffect(() => {
    handlers.current = { onComplete, onChange };
  });

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const code = digits.join('');
    handlers.current.onChange?.(code);
    if (code.length < LENGTH) {
      completed.current = null;
      return undefined;
    }
    if (completed.current === code) return undefined;
    const timer = window.setTimeout(() => {
      completed.current = code;
      handlers.current.onComplete(code);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [digits]);

  function fillFrom(index: number, text: string) {
    const incoming = text.replace(/\D/g, '');
    if (!incoming) return;
    setDigits((prev) => {
      const next = [...prev];
      for (let k = 0; k < incoming.length && index + k < LENGTH; k += 1) next[index + k] = incoming[k]!;
      return next;
    });
    refs.current[Math.min(LENGTH - 1, index + incoming.length)]?.focus();
  }

  return (
    <div className="otp" role="group" aria-label={label}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={LENGTH}
          aria-label={`${index + 1}번째 숫자`}
          value={digit}
          disabled={disabled}
          onChange={(event) => {
            const raw = event.target.value.replace(/\D/g, '');
            // Typing over a filled box gives two characters: keep the new one.
            const value = digit && raw.length === 2 && raw.includes(digit) ? raw.replace(digit, '') : raw;
            if (value.length > 1) {
              fillFrom(index, value);
              return;
            }
            setDigits((prev) => {
              const next = [...prev];
              next[index] = value.slice(-1);
              return next;
            });
            if (value && index < LENGTH - 1) refs.current[index + 1]?.focus();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' && !digit && index > 0) refs.current[index - 1]?.focus();
          }}
          onPaste={(event) => {
            const text = event.clipboardData.getData('text');
            if (/\d/.test(text)) {
              event.preventDefault();
              fillFrom(0, text.slice(0, LENGTH * 2));
            }
          }}
        />
      ))}
    </div>
  );
}
